import { apiFetch } from './client.ts';
import type { QueueEntry, RequestMode, Session, Singer, Song } from './types.ts';

/**
 * `GET/POST/PATCH/DELETE /api/rotation/*` and `/api/queue/*` (BE-13 Wave 2 Builder A). Mirrors
 * the snapshot shape `strapi-template/src/api/rotation/services/rotation.js`'s `getSnapshot`
 * actually returns (`queueEntryDto`/`songDto` for the leaf shapes) — see that file for the
 * authoritative definition; these types are a hand-kept mirror, not a shared package
 * (ARCHITECTURE.md §3: no shared types between `web/` and `strapi-template/`).
 *
 * Every write here returns the FULL new snapshot (`{data: RotationSnapshot, meta}`), so callers
 * replace their state wholesale rather than refetching — same pattern `api/playback.ts` uses for
 * `patchPlaybackState`. Every write is `retry: false` (mutating requests must never be silently
 * replayed — `client.ts`'s own doc comment on `ApiFetchOptions.retry`).
 *
 * MVP-212: the DTOs below are `Pick<>` projections of `./types.ts`'s shapes (plus the fields that
 * are genuinely rotation-only), not a second hand-copied set of interfaces.
 *
 * MVP-626 (web half): every call here carries `?room=` — not written at these call sites but
 * appended centrally by `client.ts`'s `withRoomScope`, which is why nothing below mentions it. The
 * param is deliberately INERT today: `rotation.js`/`queue.js` still call `getActiveSession()` with
 * no argument and Strapi ignores an unknown query param, so this is a no-op until MVP-626's backend
 * half ships and the controllers read the operand. It lands first on purpose — a filter that
 * shipped before anything fed it is what MVP-612 spent a commit undoing. `rotation.test.ts` gates
 * the coverage so a call added later cannot quietly escape the scope.
 */

export type RotationRequestMode = RequestMode;

export interface RotationFile {
  url: string;
  mime: string | null;
}

export type RotationSession = Pick<Session, 'documentId' | 'requestMode' | 'songsPerRotation' | 'turnaroundSec'> &
  Required<Pick<Session, 'name' | 'venue' | 'joinCode' | 'marqueeText' | 'stageMessage'>>;

export type RotationSong = Pick<Song, 'documentId' | 'title' | 'kind'> &
  Required<Pick<Song, 'artistNameCache' | 'durationMs'>> & {
    /** Playable file for the player's gapless prefetch (`usePlaybackEngine` reads `song.media.url`). */
    media: RotationFile | null;
    /** `.cdg` sidecar when `kind === 'audio_cdg'`. */
    mediaCdg: RotationFile | null;
    poster: { url: string } | null;
    isBreakMusic: boolean;
    alreadySungTonight: boolean;
  };

export type RotationQueueEntryStatus = QueueEntry['status'];

export type RotationQueueEntry = Pick<QueueEntry, 'documentId' | 'rank' | 'keyShift' | 'status' | 'source'> &
  Required<Pick<QueueEntry, 'addedAt' | 'startedAt' | 'endedAt'>> & {
    playedKeyShift: number | null;
    song: RotationSong | null;
  };

// `isRegular`/`defaultKeyShift` are hand-written: `Required<Pick<>>` would keep types.ts's `| null`,
// but the server coerces them (`Boolean(...)`, `?? 0`) and never sends null on this endpoint.
export type RotationSinger = Pick<Singer, 'documentId' | 'name'> &
  Required<Pick<Singer, 'colorTag'>> & {
    isRegular: boolean;
    defaultKeyShift: number;
    totalSongs: number;
  };

/** The one name StageRoot's flattened rows and the three stage components share — a queue entry
 * with the singer nested down from its slot (the DTO only carries singer on the slot). */
export type RotationQueueRow = RotationQueueEntry & { singer: RotationSinger | null };

export interface RotationSlot {
  documentId: string;
  rank: number;
  isPaused: boolean;
  songsThisRotation: number;
  joinedAt: string | null;
  isCurrent: boolean;
  isNextUp: boolean;
  etaSec: number | null;
  singer: RotationSinger | null;
  queue: RotationQueueEntry[];
}

export interface RotationUndoCounts {
  undoable: number;
  redoable: number;
}

export interface RotationSnapshot {
  seq: number;
  session: RotationSession;
  current: RotationQueueEntry | null;
  nextEligibleSlotId: string | null;
  slots: RotationSlot[];
  undo: RotationUndoCounts;
}

interface Envelope<TMeta = Record<string, unknown>> {
  data: RotationSnapshot;
  meta: TMeta;
}

export interface RotationResult<TMeta = Record<string, unknown>> {
  snapshot: RotationSnapshot;
  meta: TMeta;
}

async function unwrap<TMeta = Record<string, unknown>>(promise: Promise<Envelope<TMeta>>): Promise<RotationResult<TMeta>> {
  const res = await promise;
  return { snapshot: res.data, meta: res.meta };
}

export type AddSingerMode = 'fair' | 'next' | 'bottom' | 'position';

export interface AddSingerBody {
  name: string;
  mode: AddSingerMode;
  position?: number;
}

export interface MoveTargetBody {
  beforeId?: string;
  afterId?: string;
}

export interface PatchSlotBody {
  isPaused?: boolean;
  name?: string;
}

export type CreateQueueEntryMode = 'fair' | 'next' | 'bottom' | 'position';

export interface CreateQueueEntryBody {
  singerId: string;
  songId: string;
  keyShift?: number;
  mode: CreateQueueEntryMode;
}

export interface PatchQueueEntryBody {
  keyShift?: number;
  status?: RotationQueueEntryStatus;
}

export interface MoveQueueEntryBody {
  beforeId?: string;
  afterId?: string;
  singerId?: string;
}

export interface PatchSessionCurrentBody {
  songsPerRotation?: number;
  turnaroundSec?: number;
  requestMode?: RotationRequestMode;
  marqueeText?: string | null;
  stageMessage?: string | null;
  /** MVP-678. The venue passcode, and the only way to set it outside the Strapi admin panel. The
   * string IS the switch: non-empty arms the gate, empty clears and disarms it — the server refuses
   * to let `passcodeEnabled` and `passcode` disagree, because a gate with no correct answer locks
   * the shop out of its own night. Never echoed back through `applySession`; see `TonightSettings`. */
  passcode?: string;
}

/** `GET /api/rotation/state` — the resync target every other call here folds into via `seq`. */
export function getRotationState(): Promise<RotationResult<{ serverTimeMs: number }>> {
  return unwrap(apiFetch<Envelope<{ serverTimeMs: number }>>('/api/rotation/state'));
}

/** `POST /api/rotation/singers`. */
export function addSinger(body: AddSingerBody): Promise<RotationResult<{ slotId: string }>> {
  return unwrap(
    apiFetch<Envelope<{ slotId: string }>>('/api/rotation/singers', {
      method: 'POST',
      body: { data: body },
      retry: false,
    })
  );
}

/** `PATCH /api/rotation/slots/:id/move`. */
export function moveSlot(slotId: string, body: MoveTargetBody): Promise<RotationResult> {
  return unwrap(
    apiFetch<Envelope>(`/api/rotation/slots/${encodeURIComponent(slotId)}/move`, {
      method: 'PATCH',
      body: { data: body },
      retry: false,
    })
  );
}

/** `PATCH /api/rotation/slots/:id`. */
export function patchSlot(slotId: string, body: PatchSlotBody): Promise<RotationResult> {
  return unwrap(
    apiFetch<Envelope>(`/api/rotation/slots/${encodeURIComponent(slotId)}`, {
      method: 'PATCH',
      body: { data: body },
      retry: false,
    })
  );
}

/** `DELETE /api/rotation/slots/:id`. */
export function removeSlot(slotId: string): Promise<RotationResult> {
  return unwrap(
    apiFetch<Envelope>(`/api/rotation/slots/${encodeURIComponent(slotId)}`, {
      method: 'DELETE',
      retry: false,
    })
  );
}

/** `POST /api/queue/entries`. */
export function createQueueEntry(body: CreateQueueEntryBody): Promise<RotationResult<{ entryId?: string }>> {
  return unwrap(
    apiFetch<Envelope<{ entryId?: string }>>('/api/queue/entries', {
      method: 'POST',
      body: { data: body },
      retry: false,
    })
  );
}

/** `PATCH /api/queue/entries/:id`. */
export function patchQueueEntry(entryId: string, body: PatchQueueEntryBody): Promise<RotationResult> {
  return unwrap(
    apiFetch<Envelope>(`/api/queue/entries/${encodeURIComponent(entryId)}`, {
      method: 'PATCH',
      body: { data: body },
      retry: false,
    })
  );
}

/** `PATCH /api/queue/entries/:id/move`. */
export function moveQueueEntry(entryId: string, body: MoveQueueEntryBody): Promise<RotationResult> {
  return unwrap(
    apiFetch<Envelope>(`/api/queue/entries/${encodeURIComponent(entryId)}/move`, {
      method: 'PATCH',
      body: { data: body },
      retry: false,
    })
  );
}

/** `DELETE /api/queue/entries/:id` — soft-removes (`status:'removed'`), never a hard delete. */
export function removeQueueEntry(entryId: string): Promise<RotationResult> {
  return unwrap(
    apiFetch<Envelope>(`/api/queue/entries/${encodeURIComponent(entryId)}`, {
      method: 'DELETE',
      retry: false,
    })
  );
}

/** `POST /api/rotation/advance` — closes the current turn (`finishAs:'done'`) and promotes the
 * next eligible slot. `meta.advanced` is `false` when nothing was queued to promote to. */
export function advance(): Promise<RotationResult<{ advanced: boolean; current: RotationQueueEntry | null }>> {
  return unwrap(
    apiFetch<Envelope<{ advanced: boolean; current: RotationQueueEntry | null }>>('/api/rotation/advance', {
      method: 'POST',
      retry: false,
    })
  );
}

/** `POST /api/rotation/skip` — same as `advance` but closes the current turn `finishAs:'skipped'`. */
export function skip(): Promise<RotationResult<{ advanced: boolean; current: RotationQueueEntry | null }>> {
  return unwrap(
    apiFetch<Envelope<{ advanced: boolean; current: RotationQueueEntry | null }>>('/api/rotation/skip', {
      method: 'POST',
      retry: false,
    })
  );
}

/** `POST /api/rotation/undo` — a 409 `NOTHING_TO_UNDO` surfaces as an `ApiError` (`client.ts`),
 * same as every other error code; callers check `err.code`. */
export function undo(): Promise<RotationResult<{ op: string; entity: string; seq: number }>> {
  return unwrap(
    apiFetch<Envelope<{ op: string; entity: string; seq: number }>>('/api/rotation/undo', {
      method: 'POST',
      retry: false,
    })
  );
}

/** `POST /api/rotation/redo` — 409 `NOTHING_TO_REDO` on an empty redo stack. */
export function redo(): Promise<RotationResult<{ op: string; entity: string; seq: number }>> {
  return unwrap(
    apiFetch<Envelope<{ op: string; entity: string; seq: number }>>('/api/rotation/redo', {
      method: 'POST',
      retry: false,
    })
  );
}

/**
 * MVP-249 — the break/filler-music bus (SPEC AC-Q-10). All three routes have existed and been
 * granted to the kj role since BE-13; nothing on the client had ever called them.
 *
 * `POST /api/rotation/filler/start` picks a `isBreakMusic:true, status:'ready'` song, points
 * `playback-state` at it with `mode:'filler'`, and opens a `play-history` row with
 * `source:'filler'` — which is how AC-Q-10's "never appears in singer history and never counts as
 * a play" is satisfied: the row is there for the session summary but carries no singer and no
 * queue entry. `404 NO_BREAK_MUSIC` when nothing is marked yet; callers surface that as the
 * panel's empty state rather than an error toast.
 */
export function startFiller(): Promise<RotationResult<{ song: RotationSong; playHistoryId: string }>> {
  return unwrap(
    apiFetch<Envelope<{ song: RotationSong; playHistoryId: string }>>('/api/rotation/filler/start', {
      method: 'POST',
      retry: false,
    })
  );
}

/** `POST /api/rotation/filler/stop` — closes the open filler `play-history` row and resets
 * playback to idle. Safe to call when no filler is running: the service simply finds no open row. */
export function stopFiller(): Promise<RotationResult> {
  return unwrap(apiFetch<Envelope>('/api/rotation/filler/stop', { method: 'POST', retry: false }));
}

/** `GET /api/rotation/filler/queue` — a shuffled preview of what the bus would play next.
 * Deliberately NOT `unwrap`ped: this is the one rotation route whose `data` is a song list rather
 * than a full snapshot, so folding it into `RotationResult` would lie about its shape. Read-only,
 * so the default retry policy is fine. */
export function getFillerQueue(limit?: number): Promise<RotationSong[]> {
  const query = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
  return apiFetch<{ data: RotationSong[] }>(`/api/rotation/filler/queue${query}`).then((res) => res.data);
}

/** `PATCH /api/session/current` — session-level settings (`songsPerRotation` etc.) that change
 * every slot's ETA, so the controller broadcasts a fresh snapshot on this call too. */
export function patchSessionCurrent(body: PatchSessionCurrentBody): Promise<RotationResult> {
  return unwrap(
    apiFetch<Envelope>('/api/session/current', {
      method: 'PATCH',
      body: { data: body },
      retry: false,
    })
  );
}
